import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LABELS, RISK_EVENT_KINDS, type RiskEventKind } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { Button, ErrorNote } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { useOpenRiskEvents, useRiskEvents } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import type { RiskEventRow } from "@/lib/types";

/*
 * Eventi di rischio.
 *
 * Due schede, e sono due domande diverse: «che cosa è rotto ADESSO» e «è già
 * successo?». La prima è corta per costruzione — gli informativi non ci entrano
 * e chi rientra da sé si chiude da sé — e se diventa lunga il problema non è la
 * lista.
 *
 * Un evento non si cancella e non si modifica: è una prova. L'unica azione è
 * la presa in carico, che lo toglie dagli aperti e lascia scritto CHI se n'è
 * occupato.
 */

const TABS = [
  { key: "aperti", label: "Aperti" },
  { key: "storico", label: "Storico" },
] as const;

const PAGE = 50;

export function Events() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get("vista")) ?? TABS[0];

  return (
    <>
      <PageHeader title="Eventi" subtitle="Che cosa è rotto adesso, e che cosa è successo" />

      <div className="border-b border-line px-6">
        <nav className="flex gap-1" aria-label="Eventi">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (t.key === "aperti") next.delete("vista");
                else next.set("vista", t.key);
                setParams(next, { replace: true });
              }}
              aria-current={t.key === tab.key ? "page" : undefined}
              className={[
                "-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors",
                t.key === tab.key ? "border-ink font-medium text-ink" : "border-transparent text-muted hover:text-ink",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab.key === "aperti" ? <Open /> : <History />}
    </>
  );
}

// ---------------------------------------------------------------------------

function Open() {
  const q = useOpenRiskEvents();

  if (q.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (q.error) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{errorMessage(q.error)}</ErrorNote>
      </div>
    );
  }
  if (!q.data?.rows.length) {
    return (
      <EmptyState
        title="Niente di rotto"
        hint="Gli eventi informativi — un conto bruciato, per esempio — non compaiono qui: non c'è niente da chiudere. Lo storico li tiene tutti."
      />
    );
  }

  return (
    <div className="space-y-3 px-6 py-4">
      <p className="text-sm text-muted">
        {q.data.counts.critical > 0 && <span className="text-loss">{q.data.counts.critical} critici</span>}
        {q.data.counts.critical > 0 && q.data.counts.warning > 0 && " · "}
        {q.data.counts.warning > 0 && <span className="text-warn">{q.data.counts.warning} avvisi</span>}
      </p>
      {q.data.rows.map((e) => (
        <EventCard key={e.id} e={e} />
      ))}
    </div>
  );
}

/** Un evento aperto: che cos'è, dove, e il pulsante che lo chiude. */
export function EventCard({ e, compact = false }: { e: RiskEventRow; compact?: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.risk.resolve(e.id);
      await Promise.all([qc.invalidateQueries({ queryKey: ["risk"] }), qc.invalidateQueries({ queryKey: ["sessions"] })]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`card ${compact ? "p-3" : "p-4"} ${e.severity === "CRITICAL" ? "border-loss/40" : "border-warn/40"}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <SeverityDot severity={e.severity} />
        <span className="font-medium text-ink">{LABELS.riskEventKind[e.kind]}</span>
        <span className="text-xs text-faint">{dateTime(e.createdAt)}</span>
        {e.account && (
          <span className={e.account.role === "PROP" ? "text-prop" : "text-broker"}>
            {e.account.label} <span className="machine-id text-xs">{e.account.mt5Login}</span>
          </span>
        )}
        {e.session && (
          <Link to={`/fasi/${e.session.id}`} className="text-muted hover:text-ink hover:underline">
            {e.session.client.name} · {e.session.canonicalSymbol}
          </Link>
        )}
        <div className="ml-auto">
          <Button size="sm" variant="ghost" onClick={resolve} disabled={busy} title="Esce dagli aperti, con il tuo nome accanto. La riga resta.">
            {busy ? "…" : "Me ne occupo io"}
          </Button>
        </div>
      </div>
      <p className="mt-1 text-sm text-muted">{e.message}</p>
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

function SeverityDot({ severity }: { severity: RiskEventRow["severity"] }) {
  const tone = severity === "CRITICAL" ? "bg-loss" : severity === "WARNING" ? "bg-warn" : "bg-muted";
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${tone}`} title={LABELS.riskSeverity[severity]} aria-label={LABELS.riskSeverity[severity]} />;
}

// ---------------------------------------------------------------------------

function History() {
  const [kind, setKind] = useState<RiskEventKind | "">("");
  const [severity, setSeverity] = useState<"" | "INFO" | "WARNING" | "CRITICAL">("");
  const [state, setState] = useState<"" | "open" | "resolved">("");
  const [offset, setOffset] = useState(0);

  const q = useRiskEvents({
    ...(kind ? { kind: [kind] } : {}),
    ...(severity ? { severity: [severity] } : {}),
    ...(state ? { state } : {}),
    limit: PAGE,
    offset,
  });

  const reset = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setOffset(0);
  };

  return (
    <div className="space-y-4 px-6 py-4">
      <div className="flex flex-wrap gap-3">
        <select value={kind} onChange={(e) => reset(setKind)(e.target.value as RiskEventKind | "")} className="rounded border border-line bg-surface px-2 py-1.5 text-sm">
          <option value="">Tutti i tipi</option>
          {RISK_EVENT_KINDS.map((k) => (
            <option key={k} value={k}>
              {LABELS.riskEventKind[k]}
            </option>
          ))}
        </select>
        <select value={severity} onChange={(e) => reset(setSeverity)(e.target.value as typeof severity)} className="rounded border border-line bg-surface px-2 py-1.5 text-sm">
          <option value="">Tutte le gravità</option>
          {(["CRITICAL", "WARNING", "INFO"] as const).map((s) => (
            <option key={s} value={s}>
              {LABELS.riskSeverity[s]}
            </option>
          ))}
        </select>
        <select value={state} onChange={(e) => reset(setState)(e.target.value as typeof state)} className="rounded border border-line bg-surface px-2 py-1.5 text-sm">
          <option value="">Aperti e chiusi</option>
          <option value="open">Solo aperti</option>
          <option value="resolved">Solo chiusi</option>
        </select>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : q.error ? (
        <ErrorNote>{errorMessage(q.error)}</ErrorNote>
      ) : !q.data?.rows.length ? (
        <p className="card px-4 py-4 text-sm text-muted">Nessun evento con questi filtri.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-faint">
                  <th className="py-2 pr-4 font-medium">Quando</th>
                  <th className="py-2 pr-4 font-medium">Tipo</th>
                  <th className="py-2 pr-4 font-medium">Dove</th>
                  <th className="py-2 pr-4 font-medium">Che cosa</th>
                  <th className="py-2 font-medium">Chiuso</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((e) => (
                  <tr key={e.id} className="border-b border-line/60 align-top">
                    <td className="py-2.5 pr-4 whitespace-nowrap text-muted">{dateTime(e.createdAt)}</td>
                    <td className="py-2.5 pr-4">
                      <span className="flex items-baseline gap-2">
                        <SeverityDot severity={e.severity} />
                        <span className="text-ink">{LABELS.riskEventKind[e.kind]}</span>
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-muted">
                      {e.account?.label ?? "—"}
                      {e.session && (
                        <>
                          {" · "}
                          <Link to={`/fasi/${e.session.id}`} className="hover:text-ink hover:underline">
                            {e.session.client.name}
                          </Link>
                        </>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-muted">{e.message}</td>
                    <td className="py-2.5 text-xs text-faint">
                      {!e.resolvedAt ? (
                        <span className="text-warn">aperto</span>
                      ) : e.resolvedBy ? (
                        /* Preso in carico: c'è un nome. Senza nome è rientrato da sé. */
                        <>
                          {dateTime(e.resolvedAt)}
                          <br />
                          {e.resolvedBy.fullName ?? e.resolvedBy.email}
                        </>
                      ) : (
                        <>
                          {dateTime(e.resolvedAt)}
                          <br />
                          da sé
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager total={q.data.total} limit={q.data.limit} offset={offset} onOffset={setOffset} />
        </>
      )}
    </div>
  );
}

/** Avanti e indietro, col totale: si sa sempre quanto c'è dietro. */
export function Pager({ total, limit, offset, onOffset }: { total: number; limit: number; offset: number; onOffset: (n: number) => void }) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="flex items-center gap-3 text-sm text-muted">
      <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => onOffset(Math.max(0, offset - limit))}>
        Precedenti
      </Button>
      <span>
        {from}–{to} di {total}
      </span>
      <Button size="sm" variant="ghost" disabled={to >= total} onClick={() => onOffset(offset + limit)}>
        Successivi
      </Button>
    </div>
  );
}
